/**
 * Preset Layouts for Dashboard
 *
 * Each preset has either:
 *   - `layout` (flat array) — single-row arrangement, e.g. ['bible','votd']
 *   - `layoutGrid` (2D array of rows) — multi-row arrangement, e.g.
 *     [['bible'], ['notes','highlights']] for "1 top + 2 bottom"
 *
 * `shape` is used by PresetLayoutSelector to render a tiny SVG preview so
 * users can SEE the arrangement before applying. `shape` mirrors the grid
 * but each cell is a placeholder block — pane IDs are independent.
 */

const flat = (panes) => ({
  layout: panes,
  layoutGrid: null,
  shape: [panes.map(() => 1)], // single row, N equal blocks
});

const grid = (panesByRow) => ({
  layout: panesByRow.flat(),
  layoutGrid: panesByRow,
  shape: panesByRow.map((row) => row.map(() => 1)),
});

export const PRESET_LAYOUTS = {
  // ── classic single-row presets (legacy compat) ───────────────────────
  reading: {
    id: 'reading',
    name: { en: 'Reading Mode', es: 'Modo Lectura' },
    description: {
      en: 'Focus on reading with minimal distractions',
      es: 'Enfócate en leer con mínimas distracciones',
    },
    icon: '📖',
    ...flat(['bible', 'votd', 'reading-plans']),
  },
  study: {
    id: 'study',
    name: { en: 'Study Mode', es: 'Modo Estudio' },
    description: {
      en: 'Deep dive with commentaries and concordance',
      es: 'Profundiza con comentarios y concordancia',
    },
    icon: '📚',
    ...flat(['bible', 'concordance', 'commentary', 'dictionary']),
  },
  research: {
    id: 'research',
    name: { en: 'Research Mode', es: 'Modo Investigación' },
    description: {
      en: 'Advanced tools for theological research',
      es: 'Herramientas avanzadas para investigación teológica',
    },
    icon: '🔍',
    ...flat(['bible', 'interlinear', 'parallel', 'crossrefs', 'wordstudy']),
  },
  devotional: {
    id: 'devotional',
    name: { en: 'Devotional Mode', es: 'Modo Devocional' },
    description: {
      en: 'Personal reflection and spiritual growth',
      es: 'Reflexión personal y crecimiento espiritual',
    },
    icon: '🙏',
    ...flat(['bible', 'devotional', 'notes', 'highlights']),
  },
  teaching: {
    id: 'teaching',
    name: { en: 'Teaching Mode', es: 'Modo Enseñanza' },
    description: {
      en: 'Prepare lessons with comprehensive tools',
      es: 'Prepara lecciones con herramientas completas',
    },
    icon: '👨‍🏫',
    ...flat(['bible', 'commentary', 'characters', 'maps', 'harmony']),
  },
  minimal: {
    id: 'minimal',
    name: { en: 'Minimal Mode', es: 'Modo Minimalista' },
    description: { en: 'Just the Bible, nothing else', es: 'Solo la Biblia, nada más' },
    icon: '✨',
    ...flat(['bible']),
  },

  // ── multi-row grid presets ───────────────────────────────────────────
  topVerseBottomStudy: {
    id: 'topVerseBottomStudy',
    name: { en: 'Verse on Top, Study Below', es: 'Versículo Arriba, Estudio Abajo' },
    description: {
      en: 'Wide Bible + Verse of the Day on top, two study tools below',
      es: 'Biblia amplia + Versículo del Día arriba, dos herramientas debajo',
    },
    icon: '⬆️',
    ...grid([['bible'], ['concordance', 'commentary']]),
  },
  studyTopBibleBottom: {
    id: 'studyTopBibleBottom',
    name: { en: 'Tools Up, Bible Down', es: 'Herramientas Arriba, Biblia Abajo' },
    description: {
      en: 'Two reference tools on top, wide Bible reader below',
      es: 'Dos herramientas arriba, lector bíblico amplio abajo',
    },
    icon: '⬇️',
    ...grid([['commentary', 'dictionary'], ['bible']]),
  },
  quad: {
    id: 'quad',
    name: { en: 'Quad Grid (2×2)', es: 'Cuadrícula 2×2' },
    description: {
      en: 'Four panes in a 2×2 grid for power study',
      es: 'Cuatro paneles en cuadrícula 2×2 para estudio intenso',
    },
    icon: '🟦',
    ...grid([
      ['bible', 'commentary'],
      ['notes', 'highlights'],
    ]),
  },
  sermonPrep: {
    id: 'sermonPrep',
    name: { en: 'Sermon Prep Grid', es: 'Grid Preparación de Sermón' },
    description: {
      en: 'Bible + Word Study on top, Commentary + Topical Bible below',
      es: 'Biblia + Estudio de Palabras arriba, Comentario + Temática abajo',
    },
    icon: '🎤',
    ...grid([
      ['bible', 'wordstudy'],
      ['commentary', 'topical'],
    ]),
  },
  hubAndSpoke: {
    id: 'hubAndSpoke',
    name: { en: 'Wide Bible · Three Below', es: 'Biblia Amplia · Tres Abajo' },
    description: {
      en: 'A wide Bible reader on top, three reference panes below',
      es: 'Lector bíblico amplio arriba, tres paneles abajo',
    },
    icon: '🔭',
    ...grid([['bible'], ['concordance', 'crossrefs', 'wordstudy']]),
  },
  parallelDeepDive: {
    id: 'parallelDeepDive',
    name: { en: 'Parallel Deep Dive', es: 'Comparación Profunda' },
    description: {
      en: 'Parallel translations on top, interlinear + cross-refs below',
      es: 'Traducciones paralelas arriba, interlineal + referencias abajo',
    },
    icon: '🔄',
    ...grid([['parallel'], ['interlinear', 'crossrefs']]),
  },
  threeStack: {
    id: 'threeStack',
    name: { en: 'Triple Stack', es: 'Triple Pila' },
    description: {
      en: 'Three full-width rows — Bible, Notes, Highlights',
      es: 'Tres filas completas — Biblia, Notas, Resaltados',
    },
    icon: '📚',
    ...grid([['bible'], ['notes'], ['highlights']]),
  },
};

export const getPresetLayout = (presetId) => PRESET_LAYOUTS[presetId];
export const getAllPresets = () => Object.values(PRESET_LAYOUTS);
